const VERIFY = "[VERIFY WITH SABAKT] Detailed technical specifications, grades and available dimensions to be confirmed by SABAKT engineering.";

export const PRODUCT_CATEGORIES = [
  {
    slug: "concrete-block-wear-parts",
    name: "Concrete & Block Plant Wear Parts",
    blurb: "High-performance wear-resistant components for concrete batching and block manufacturing plants — reverse-engineered, site-measured and compatible with Liebherr, BHS, SICOMA, ELBA, MASA, HESS, MEKA and other leading brands.",
    image: "/images/concrete/b09.jpg",
  },
  {
    slug: "cement-crusher-wear-solutions",
    name: "Cement & Crusher Wear Solutions",
    blurb: "Wear parts, hardfacing and reconditioning for cement plants, crushers and crushing units — engineered with technical support from Diffusion Engineers Limited.",
    image: "/images/cement/c05.jpg",
  },
  {
    slug: "steel-fabrication-products",
    name: "Steel Fabrication Products",
    blurb: "Structural and mechanical steel fabrication manufactured strictly as per approved drawings or dimensions taken directly from site.",
    image: "/images/steel/s16.jpg",
  },
  {
    slug: "tanks-silos-vessels",
    name: "Tanks, Silos & Vessels",
    blurb: "Diesel tanks, water tanks, stainless steel tanks, silos, hoppers and bins — fabricated to project requirements, capacity and site conditions.",
    image: "/images/steel/s53.jpg",
  },
];

export const PRODUCTS = [
  // ── Concrete & Block Plant Wear Parts ─────────────────────────────
  {
    slug: "mixer-arms",
    name: "Mixer Arms",
    category: "concrete-block-wear-parts",
    image: "/images/concrete/b09.jpg",
    gallery: ["/images/concrete/b09.jpg", "/images/concrete/b20.jpg", "/images/concrete/b28.jpg", "/images/concrete/b32.jpg"],
    brands: "Liebherr · BHS · SICOMA · ELBA · MASA · HESS · MEKA",
    overview: "Cast and fabricated mixer arms manufactured as direct replacement parts for leading concrete mixer brands — dimensionally verified against OEM parts or site-measured samples.",
    applications: ["Concrete batching plants", "Twin-shaft & planetary mixers", "Block and precast plants"],
    benefits: ["Brand-compatible fit", "Wear-resistant cast alloy", "Reverse-engineered from sample or drawing"],
    formats: VERIFY,
  },
  {
    slug: "mixing-blades-paddles",
    name: "Mixing Blades & Paddles",
    category: "concrete-block-wear-parts",
    image: "/images/concrete/b11.jpg",
    gallery: ["/images/concrete/b11.jpg", "/images/concrete/b27.jpg", "/images/concrete/b40.jpg"],
    brands: "Liebherr · BHS · SICOMA · ELBA · MASA · HESS · MEKA",
    overview: "Mixing blades and paddles for batching-plant mixers, cast in wear-resistant material and machined to the correct mounting geometry.",
    applications: ["Concrete mixers", "Mortar & screed mixers", "Block plant mixers"],
    benefits: ["Extended wear life vs OEM-standard parts", "Correct mixing geometry preserved", "Supplied as matched sets"],
    formats: VERIFY,
  },
  {
    slug: "scrapers",
    name: "Scrapers",
    category: "concrete-block-wear-parts",
    image: "/images/concrete/b37.jpg",
    gallery: ["/images/concrete/b37.jpg", "/images/concrete/b22.jpg", "/images/concrete/b14.jpg"],
    brands: "Liebherr · BHS · SICOMA · ELBA · MASA · HESS · MEKA",
    overview: "Mixer scraper assemblies and scraper blades — the parts that keep material moving off the mixer wall and floor before buildup becomes a cleaning shutdown.",
    applications: ["Mixer wall & floor scraping", "Batching plant mixers", "High-buildup applications"],
    benefits: ["Reduces material buildup", "Protects mixer floor liners", "Brand-compatible mounting"],
    formats: VERIFY,
  },
  {
    slug: "side-bottom-liners",
    name: "Side & Bottom Liners",
    category: "concrete-block-wear-parts",
    image: "/images/concrete/b13.jpg",
    gallery: ["/images/concrete/b13.jpg", "/images/concrete/b12.jpg", "/images/concrete/b29.jpg", "/images/concrete/b45.jpg"],
    overview: "Cast wear liners for mixer walls and floors — side liners, bottom liners and corner liners drilled to the existing bolt pattern for fast replacement during planned stops.",
    applications: ["Mixer side walls", "Mixer floors", "High-abrasion mixing zones"],
    benefits: ["Drilled to existing bolt pattern", "Wear-resistant cast alloy", "Replaceable liner system"],
    formats: VERIFY,
  },
  {
    slug: "table-sheets",
    name: "Table Sheets",
    category: "concrete-block-wear-parts",
    image: "/images/concrete/b26.jpg",
    gallery: ["/images/concrete/b26.jpg", "/images/concrete/b23.jpg", "/images/concrete/b24.jpg", "/images/concrete/b47.jpg"],
    overview: "Mixer table sheets and floor discs — precision-cut and drilled wear surfaces forming the working floor of the mixer.",
    applications: ["Mixer floors", "Rotating table surfaces", "High-abrasion floor zones"],
    benefits: ["Precision cut & drilled", "Segmented for partial replacement", "Wear-matched material"],
    formats: VERIFY,
  },
  {
    slug: "running-wheels-rollers",
    name: "Running Wheels & Rollers",
    category: "concrete-block-wear-parts",
    image: "/images/concrete/b06.jpg",
    gallery: ["/images/concrete/b06.jpg", "/images/concrete/b08.jpg"],
    overview: "Running wheels, rollers and machined rotating components for mixers and plant machinery — machined to specification with correct bore and keyway geometry.",
    applications: ["Mixer drive systems", "Conveyor & plant rollers", "Rotating machinery"],
    benefits: ["Machined to specification", "Material matched to duty", "Bore & keyway to drawing"],
    formats: VERIFY,
  },
  {
    slug: "custom-wear-plates",
    name: "Custom Wear Plates",
    category: "concrete-block-wear-parts",
    image: "/images/concrete/b30.jpg",
    gallery: ["/images/concrete/b30.jpg", "/images/concrete/b02.jpg", "/images/concrete/b21.jpg"],
    overview: "Customized wear plates manufactured as per drawing or site measurement — for hoppers, chutes, bins and any high-impact, high-abrasion zone in the plant.",
    applications: ["Hoppers & bins", "Chutes & transfer points", "Aggregate handling zones"],
    benefits: ["Made to drawing or site measurement", "Abrasion-resistant materials", "Install-ready supply"],
    formats: VERIFY,
  },
  // ── Cement & Crusher Wear Solutions ───────────────────────────────
  {
    slug: "crusher-wear-parts",
    name: "Crusher Wear Parts",
    category: "cement-crusher-wear-solutions",
    image: "/images/cement/c05.jpg",
    gallery: ["/images/cement/c05.jpg", "/images/cement/c01.jpg", "/images/cement/c21.jpg"],
    overview: "Jaw plates, liners and wear components for crushers and crushing units — supplied and reconditioned with hardfacing where the duty demands it.",
    applications: ["Jaw crushers", "Impact crushers", "Cement plant crushing lines"],
    benefits: ["Wear-matched material selection", "Hardfaced options for severe duty", "Reconditioning available"],
    formats: VERIFY,
  },
  {
    slug: "hammers-blow-bars",
    name: "Hammers & Blow Bars",
    category: "cement-crusher-wear-solutions",
    image: "/images/cement/c14.jpg",
    gallery: ["/images/cement/c14.jpg", "/images/cement/c04.jpg"],
    overview: "Crusher hammers and blow bars — new manufacture and reconditioning of worn units through rebuilding and application-matched hardfacing.",
    applications: ["Impact crushers", "Hammer mills", "Clinker crushers"],
    benefits: ["Recondition worn units instead of replacing", "Hardfaced working edges", "Dimensional restoration"],
    formats: VERIFY,
  },
  {
    slug: "hardfaced-wear-plates",
    name: "Hardfaced Wear Plates",
    category: "cement-crusher-wear-solutions",
    image: "/images/cement/c09.jpg",
    gallery: ["/images/cement/c09.jpg", "/images/cement/c02.jpg", "/images/cement/c08.jpg"],
    overview: "Hardfaced and overlay wear plates produced with specialized welding consumables from Diffusion Engineers Limited — for chutes, hoppers, ducts and severe-abrasion zones.",
    applications: ["Chute & hopper liners", "Ducts & fan casings", "Transfer points"],
    benefits: ["Severe-abrasion resistance", "Overlay matched to wear mechanism", "Cut and drilled to fit"],
    formats: VERIFY,
  },
  {
    slug: "impeller-fan-reconditioning",
    name: "Impeller Fan Reconditioning",
    category: "cement-crusher-wear-solutions",
    image: "/images/cement/c10.jpg",
    gallery: ["/images/cement/c10.jpg", "/images/cement/c11.jpg"],
    overview: "Reconditioning of worn impeller fans and rotating components — blade rebuilding, hardfacing of wear zones and dimensional restoration.",
    applications: ["Cement plant fans", "Industrial blowers", "Dust extraction impellers"],
    benefits: ["Fraction of replacement cost", "Restored blade profiles", "Hardfaced wear zones"],
    formats: VERIFY,
  },
  {
    slug: "augers-screw-conveyors",
    name: "Augers & Screw Conveyors",
    category: "cement-crusher-wear-solutions",
    image: "/images/cement/c03.jpg",
    gallery: ["/images/cement/c03.jpg", "/images/cement/c07.jpg"],
    overview: "Manufacture and hardfacing of augers and screw conveyor flights — the highest-wear components in powder and aggregate handling.",
    applications: ["Cement & fly-ash screws", "Aggregate conveyors", "Feed augers"],
    benefits: ["Hardfaced flight edges", "New manufacture or reconditioning", "Dimensional restoration"],
    formats: VERIFY,
  },
  {
    slug: "screen-trommel-components",
    name: "Screen & Trommel Components",
    category: "cement-crusher-wear-solutions",
    image: "/images/cement/c13.jpg",
    gallery: ["/images/cement/c13.jpg"],
    overview: "Fabrication and repair of screening drums, trommel components and mesh support structures for crushing and screening plants.",
    applications: ["Trommel screens", "Screening plants", "Quarry & crusher circuits"],
    benefits: ["Structural repair & re-meshing", "Fabricated to site dimensions", "Wear-zone reinforcement"],
    formats: VERIFY,
  },
  {
    slug: "bucket-wear-packages",
    name: "Bucket Wear Packages",
    category: "cement-crusher-wear-solutions",
    image: "/images/cement/c18.jpg",
    gallery: ["/images/cement/c18.jpg", "/images/cement/c19.jpg", "/images/cement/c20.jpg", "/images/cement/c16.jpg"],
    overview: "Complete wear packages for excavator and loader buckets — hardfacing patterns, lip and edge restoration, heel and side protection for quarry and mining duty.",
    applications: ["Excavator buckets", "Loader buckets", "Mining & quarry fleets"],
    benefits: ["Extended bucket service life", "Engineered hardfacing patterns", "Sequential fleet programs"],
    formats: VERIFY,
  },
  // ── Steel Fabrication Products ────────────────────────────────────
  {
    slug: "platforms-catwalks-stairs",
    name: "Platforms, Catwalks & Stairs",
    category: "steel-fabrication-products",
    image: "/images/steel/s26.jpg",
    gallery: ["/images/steel/s26.jpg", "/images/steel/s27.jpg", "/images/steel/s42.jpg"],
    overview: "Access platforms, catwalks, steel stairs and ladders — fabricated to approved drawings or site-taken dimensions, with handrails and safety details included.",
    applications: ["Plant access structures", "Equipment platforms", "Maintenance walkways"],
    benefits: ["First-time fit from site measurement", "Complete with handrails", "Painted or galvanized finish"],
    formats: VERIFY,
  },
  {
    slug: "structural-frames-supports",
    name: "Structural Frames & Supports",
    category: "steel-fabrication-products",
    image: "/images/steel/s16.jpg",
    gallery: ["/images/steel/s16.jpg", "/images/steel/s35.jpg", "/images/steel/s50.jpg"],
    overview: "Heavy structural frames, equipment supports, pipe supports and industrial steelwork — welded, drilled and finished in our Riyadh workshop.",
    applications: ["Equipment structures", "Pipe supports", "Industrial frames"],
    benefits: ["Heavy-duty welding", "Fabricated to approved drawings", "Quality-compliance checks"],
    formats: VERIFY,
  },
  {
    slug: "brackets-embedded-plates",
    name: "Brackets & Embedded Plates",
    category: "steel-fabrication-products",
    image: "/images/steel/s34.jpg",
    gallery: ["/images/steel/s34.jpg", "/images/steel/s43.jpg", "/images/steel/s30.jpg"],
    overview: "Steel brackets, embedded plates and connection components for construction and industrial projects — cut, drilled and finished to specification.",
    applications: ["Concrete embedments", "Structural connections", "Facade & equipment brackets"],
    benefits: ["Precision cutting & drilling", "Batch production", "Galvanizing available"],
    formats: VERIFY,
  },
  {
    slug: "lifting-attachments",
    name: "Crane Forks, Man Baskets & Cages",
    category: "steel-fabrication-products",
    image: "/images/steel/s19.jpg",
    gallery: ["/images/steel/s19.jpg", "/images/steel/s20.jpg"],
    overview: "Site lifting attachments — crane forks, man baskets, goods-carrying cages and crane loading platforms — fabricated for safe material and personnel handling.",
    applications: ["Construction sites", "Industrial material handling", "Crane operations"],
    benefits: ["Engineered weldment construction", "Built to site requirements", "Load-rated design [VERIFY WITH SABAKT]"],
    formats: VERIFY,
  },
  {
    slug: "material-handling-bins",
    name: "Stacking Stillage & Handling Bins",
    category: "steel-fabrication-products",
    image: "/images/steel/s42.jpg",
    gallery: ["/images/steel/s42.jpg", "/images/steel/s44.jpg", "/images/steel/s33.jpg"],
    overview: "Stacking stillages, brick-handling bins, form boxes and industrial containers — built for repeated daily use in block plants and yards.",
    applications: ["Block & brick handling", "Plant logistics", "Storage & stacking"],
    benefits: ["Stackable designs", "Forklift-compatible bases", "Heavy-duty construction"],
    formats: VERIFY,
  },
  {
    slug: "waste-containers-garbage-chutes",
    name: "Waste Containers & Garbage Chutes",
    category: "steel-fabrication-products",
    image: "/images/steel/s14.jpg",
    gallery: ["/images/steel/s14.jpg", "/images/steel/s17.jpg"],
    overview: "Waste containers, skips and garbage chutes for construction and industrial sites — fabricated in sizes and configurations to project requirements.",
    applications: ["Construction waste handling", "High-rise garbage chutes", "Industrial skips"],
    benefits: ["Custom capacities", "Reinforced wear zones", "Crane-liftable options"],
    formats: VERIFY,
  },
  {
    slug: "ducts-chutes",
    name: "Ducts & Chutes",
    category: "steel-fabrication-products",
    image: "/images/steel/s24.jpg",
    gallery: ["/images/steel/s24.jpg", "/images/steel/s28.jpg", "/images/steel/s25.jpg"],
    overview: "Industrial ducts, elbows, chutes and material-transfer steelwork — including stainless steel ducting and wear-lined chutes for abrasive service.",
    applications: ["Process ducting", "Material transfer chutes", "Dust & fume extraction"],
    benefits: ["Carbon or stainless steel", "Wear-liner options", "Site-dimension fabrication"],
    formats: VERIFY,
  },
  // ── Tanks, Silos & Vessels ────────────────────────────────────────
  {
    slug: "diesel-storage-tanks",
    name: "Diesel Storage Tanks",
    category: "tanks-silos-vessels",
    image: "/images/steel/s53.jpg",
    gallery: ["/images/steel/s53.jpg", "/images/steel/s56.jpg", "/images/steel/s60.jpg"],
    overview: "Diesel storage tanks fabricated to project requirements — capacity, dimensions, nozzle orientation and site conditions — complete with ladders, supports and pipe interfaces.",
    applications: ["Generator & fleet fueling", "Construction sites", "Industrial facilities"],
    benefits: ["Built to project specification", "Complete ancillary steelwork", "Designed and fabricated according to applicable project specifications and required standards"],
    formats: VERIFY,
  },
  {
    slug: "water-storage-tanks",
    name: "Water Storage Tanks",
    category: "tanks-silos-vessels",
    image: "/images/steel/s54.jpg",
    gallery: ["/images/steel/s54.jpg", "/images/steel/s59.jpg"],
    overview: "Steel water storage tanks for industrial and site applications — fabricated to capacity and site requirements with all associated steelwork.",
    applications: ["Industrial water storage", "Site facilities", "Process water"],
    benefits: ["Custom capacities", "Internal coating options [VERIFY WITH SABAKT]", "Supports, ladders & nozzles included"],
    formats: VERIFY,
  },
  {
    slug: "cement-silos",
    name: "Cement Silos",
    category: "tanks-silos-vessels",
    image: "/images/steel/s02.jpg",
    gallery: ["/images/steel/s02.jpg", "/images/steel/s22.jpg", "/images/steel/s10.jpg"],
    overview: "Cement and powder silos fabricated for batching plants and industrial storage — including cones, support structures and transport-friendly sectional designs.",
    applications: ["Concrete batching plants", "Cement & fly-ash storage", "Powder handling"],
    benefits: ["Sectional transportable designs", "Complete with support steelwork", "Fabricated to plant layout"],
    formats: VERIFY,
  },
  {
    slug: "hoppers-bins-cones",
    name: "Hoppers, Bins & Cones",
    category: "tanks-silos-vessels",
    image: "/images/steel/s04.jpg",
    gallery: ["/images/steel/s04.jpg", "/images/steel/s06.jpg", "/images/steel/s08.jpg", "/images/steel/s25.jpg"],
    overview: "Hoppers, bins, cones and dish ends — from aggregate hoppers to stainless process components, fabricated to drawing with correct flange and outlet geometry.",
    applications: ["Aggregate & material hoppers", "Process bins", "Tank ends & cones"],
    benefits: ["True-to-template forming", "Wear-liner options", "Flanges & outlets to spec"],
    formats: VERIFY,
  },
  {
    slug: "drip-trays-form-boxes",
    name: "Drip Trays & Form Boxes",
    category: "tanks-silos-vessels",
    image: "/images/steel/s33.jpg",
    gallery: ["/images/steel/s33.jpg", "/images/steel/s47.jpg"],
    overview: "Drip trays, form boxes and fabricated sheet-metal components for plants and equipment — batch-produced to consistent dimensions.",
    applications: ["Equipment drip containment", "Concrete formwork boxes", "Sheet-metal components"],
    benefits: ["Batch consistency", "Custom dimensions", "Fast repeat orders"],
    formats: VERIFY,
  },
];
